import React, { useContext, useMemo } from 'react';
import { AppContext } from '../../util/AppContext';
import { getConfigValue } from '../../util/getConfigValue';
import defaultLogo from '../../styles/assets/img/chat/logo.svg';
import { getChatTheme, resolveChatThemeName } from './chatThemes';

// Fallback values used when the matching Chat.* config entry is missing.
// List values (IntroMessage, Suggestions, Disclaimer) are '|' separated in config.
// Text wrapped in **double asterisks** is rendered bold.
const DEFAULTS = {
    AssistantName: 'Geniee',
    LauncherTitle: "I'm Geniee, Your PharmaRX Access Guide",
    IntroTitle: "Hi, I'm Geniee.",
    IntroMessage: [
        'I can help you quickly find answers about payer coverage, access restrictions, and reimbursement pathways.',
        'Think of me as your market access expert, delivering the answers you need, when you need them.',
        '**Select a suggested question or ask your own to get started.**',
    ],
    SuggestionsTitle: 'Some questions you could ask me...',
    Suggestions: [
        'Who are the **new writers** in my region?',
        'What are the **key insights** for a HCP?',
        'What are the **top payers** for a HCP?',
        'What are the **recent wins** in my region, and HCPs to target?',
        'Who are the **top HCPs** for a payer?',
        "What's the **coverage status** of a payer?",
    ],
    Disclaimer: [
        'Please do not include any personal information such as name, email or date of birth.',
        'Coverage is not guaranteed. Benefits are determined by each plan and may vary or change. Please confirm coverage with the patient’s health plan. Sources: MMIT & IQVIA, September 2026.',
    ],
    InputPlaceholder: 'Ask a question',
    LogoUrl: defaultLogo,
    LogoAlt: 'PharmaRX',
    // Markdown shown on the menu's "Terms and Privacy Policies" screen
    TermsContent: '',
};

const LIST_KEYS = ['IntroMessage', 'Suggestions', 'Disclaimer'];

/**
 * Resolves the chat branding/content from appState.config.Chat, falling back to the
 * skin's defaults (Chat.Theme, see chatThemes.js) and then DEFAULTS.
 * Chat.Enabled is treated as on unless it is explicitly set to 'false'.
 */
export function useChatConfig() {
    const [appState] = useContext(AppContext);

    return useMemo(() => {
        const chatConfig = appState?.config?.Chat;
        const read = (key) => {
            const value = getConfigValue(chatConfig?.[key], appState)?.Value;
            return typeof value === 'string' && value.trim() !== '' ? value : null;
        };

        const themeName = resolveChatThemeName(read('Theme'));
        const themeContent = getChatTheme(themeName).content;

        const resolved = { enabled: read('Enabled')?.toLowerCase() !== 'false', Theme: themeName };
        // Precedence: client config value > skin default > DEFAULTS
        Object.keys(DEFAULTS).forEach((key) => {
            const value = read(key);
            if (value === null) {
                resolved[key] = themeContent[key] ?? DEFAULTS[key];
            } else if (LIST_KEYS.includes(key)) {
                resolved[key] = value.split('|').map((s) => s.trim()).filter(Boolean);
            } else {
                resolved[key] = value;
            }
        });
        return resolved;
    }, [appState]);
}

/**
 * Renders config text, turning **bold** segments into <strong> without using innerHTML.
 */
export function RichText({ text }) {
    return String(text)
        .split(/\*\*(.+?)\*\*/g)
        .map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : <React.Fragment key={i}>{part}</React.Fragment>));
}

/**
 * Plain-text version of config text (bold markers stripped), e.g. for sending a suggestion.
 */
export function toPlainText(text) {
    return String(text).replace(/\*\*(.+?)\*\*/g, '$1');
}
